import { LOADING_STAGES, LIST_STAGES } from "../../constants/types";
import { GenericRootThunkAction, TPageType } from "../../types/common";
import backend from "../../services/axios.functions";
import { ReviewStagesState } from "../reducers/reviewStagesReducer";
import { getExecutionBrief } from "./executionBriefListActions";
import { getWorkOrder } from "./workOrderActions";
import { ReviewApprovalStatus } from "../../types/review-approval/review-approval-status.type";
import { IReviewStage } from "../../types/review-stage/ireview-stage.interface";
import * as campaignBriefStore from "../../pages/campaign-brief/store/campaign-brief.store";

export const ADD_REVIEW_STAGE = "ADD_REVIEW_STAGE";
export const REMOVE_REVIEW_STAGE = "REMOVE_REVIEW_STAGE";
export const UPDATE_REVIEW_STAGE = "UPDATE_REVIEW_STAGE";
export const REORDER_REVIEW_STAGES = "REORDER_REVIEW_STAGES";
export const SET_REVIEW_STAGE_STATUS = "SET_REVIEW_STAGE_STATUS";
export const RESET_REVIEW_STAGES = "RESET_REVIEW_STAGES";

export const getReviewStages = (id: number, pageType: TPageType): GenericRootThunkAction => async (
  dispatch
) => {
  dispatch({ type: LOADING_STAGES });
  try {
    const { data } = await backend.get(`/${pageType}/${id}/review-stages/`);
    if (!data) {
      throw new Error("No stages");
    }

    if (data.results) {
      dispatch({
        type: LIST_STAGES,
        payload: { stages: data.results.sort((a: any, b: any) => a.order - b.order) },
      });
    }
  } catch (err) {
    console.log(err);
  }
};

export const addReviewStage = (
  pageType: TPageType,
  stage: IReviewStage
): GenericRootThunkAction => async (dispatch) => {
  dispatch({ type: LOADING_STAGES });
  try {
    const { status, data: createdReviewStage } = await backend.post<IReviewStage>(
      `${pageType}-review-stages/`,
      {
        id: stage.id,
        name: stage.name,
        order: stage.order,
        status: stage.status,
        campaign_brief: stage.parentId,
        execution_brief: stage.parentId,
        work_order: stage.parentId,
        reviewer: stage.reviewer.id,
      }
    );

    if (status === 201 && createdReviewStage) {
      dispatch({ type: ADD_REVIEW_STAGE, payload: { stage: createdReviewStage } });
      dispatch(getReviewStages(stage.parentId, pageType));
    }
  } catch (err) {
    console.log(err);
  }
};

export const updateReviewStage = (
  pageType: TPageType,
  stage: IReviewStage
): GenericRootThunkAction => async (dispatch) => {
  try {
    const { status, data: updatedReviewStage } = await backend.patch<IReviewStage>(
      `${pageType}-review-stages/${stage.id}/`,
      {
        id: stage.id,
        name: stage.name,
      }
    );

    if (status === 201 && updatedReviewStage) {
      dispatch({ type: UPDATE_REVIEW_STAGE, payload: { stage: updatedReviewStage } });
      dispatch(getReviewStages(stage.parentId, pageType));
    }
  } catch (err) {
    console.log(err);
  }
};

const updateApprovedStages = (
  pageType: TPageType,
  stageId: number,
  isRemoval: boolean
): GenericRootThunkAction => async (dispatch, getState) => {
  const state = getState();
  const { stages } = state.reviewStages as ReviewStagesState;
  const campaignBriefId = state.campaignBrief.data!.id;
  const executionBriefId = state.executionBriefs.data!.id;
  const workOrderId = state.workOrders.data!.id;
  const status = "approved" as ReviewApprovalStatus;

  const currentStage = stages.find((s) => s.id === stageId);
  const oldStatus = currentStage?.status;
  if (currentStage) {
    const key = "status";
    currentStage[key] = status;
  }

  let parentId = null;
  switch (pageType) {
    case "campaign-briefs":
      parentId = campaignBriefId;
      break;
    case "execution-briefs":
      parentId = executionBriefId;
      break;
    case "work-orders":
      parentId = workOrderId;
      break;
  }

  const allStages = stages.filter((s) => s !== currentStage);
  // const areAllStagesApproved = allStages
  //  .map((stage) => (stage.id === stageId ? { ...stage } : stage))
  //  .every(({ status }) => status === "approved");
  // const checkRemovedStage = isRemoval ? allStages.length : true;
  const removeNotLastStage = isRemoval && allStages.length && oldStatus !== "approved"; // Stage is being removed, and it's not the final stage
  const notRemovalNeedsApproving = !isRemoval && oldStatus !== "approved"; // Stage is not being removed, and it's previous state is not approved

  // if (checkRemovedStage && oldStatus !== "approved") {
  if (removeNotLastStage || notRemovalNeedsApproving) {
    const baseURL = `/${pageType}-review-stages/${stageId}`;
    if (oldStatus === "needs_updating") {
      await backend.put(`${baseURL}/start-review/`);
    }
    await backend.put(`${baseURL}/approve/`);
    await dispatch({ type: SET_REVIEW_STAGE_STATUS, payload: { status, stageId } });
  }

  // re-fetch parent page to update status
  if (pageType === "campaign-briefs") {
    campaignBriefStore.fetchBrief(parentId!);
  }
  if (pageType === "execution-briefs") {
    dispatch(getExecutionBrief(String(parentId)));
  }
  if (pageType === "work-orders") {
    dispatch(getWorkOrder(String(parentId)));
  }
};

export const removeReviewStage = (
  pageType: TPageType,
  stageId: number
): GenericRootThunkAction => async (dispatch) => {
  try {
    await dispatch(updateApprovedStages(pageType, stageId, true));
    await backend.delete(`/${pageType}-review-stages/${stageId}/`);
    await dispatch({ type: REMOVE_REVIEW_STAGE, payload: { stageId } });
  } catch (err) {
    console.log(err);
  }
};

export const reorderReviewStages = (
  id: number,
  pageType: TPageType,
  orderedStageIds: number[]
): GenericRootThunkAction => async (dispatch) => {
  try {
    const { status } = await backend.patch(`/${pageType}/${id}/reorder-review-stages/`, {
      review_stage_order: orderedStageIds,
    });

    if (status === 200) {
      dispatch({ type: REORDER_REVIEW_STAGES, payload: { orderedStageIds } });
      dispatch(getReviewStages(id, pageType));
    }
  } catch (err) {
    console.log(err);
  }
};

export const changeReviewStageStatus = (
  stageId: number,
  status: ReviewApprovalStatus,
  pageType: TPageType
): GenericRootThunkAction => async (dispatch) => {
  const baseURL = `/${pageType}-review-stages/${stageId}`;
  try {
    if (status === "approved") {
      await dispatch(updateApprovedStages(pageType, stageId, false));
    }

    if (status === "needs_updating") {
      await backend.put(`${baseURL}/request-changes/`);
      dispatch({ type: SET_REVIEW_STAGE_STATUS, payload: { status, stageId } });
    }

    if (status === "ready_for_review") {
      await backend.put(`${baseURL}/start-review/`);
      dispatch({ type: SET_REVIEW_STAGE_STATUS, payload: { status, stageId } });
    }
  } catch (err) {
    console.error(err);
  }
};

export const resetReviewStages = () => ({
  type: RESET_REVIEW_STAGES,
});
